import { useState } from "react";
import { toast } from "sonner";
import { inputCls } from "./shared";

const WHATSAPP_NUMBER = "919119202607";

export const BUDGET_RANGES = ["Under ₹25L", "₹25L–₹50L", "₹50L–₹1Cr", "Above ₹1Cr"];
export const WEDDING_TYPES = ["Palace Wedding", "Beach Wedding", "Destination Wedding", "Rishikesh River Wedding", "Other"];
export const PREFERRED_LOCATIONS = ["Jaipur", "Udaipur", "Goa", "Rishikesh", "Other"];
export const EVENT_OPTIONS = ["Haldi", "Mehendi", "Sangeet", "Wedding", "Roka", "Engagement", "Baby Shower", "Corporate Event", "Social Event"];
export const ADDON_OPTIONS = ["DJ", "Anchor", "Decor Style"];

const EMPTY = {
  bride_name: "", groom_name: "", phone: "", alt_phone: "", email: "",
  city: "", wedding_date: "", budget_range: "", wedding_type: "", preferred_location: "",
  events: [], event_addons: {},
};

export default function EnquireForm() {
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleEvent = (ev) =>
    setForm((f) => {
      const has = f.events.includes(ev);
      const events = has ? f.events.filter((x) => x !== ev) : [...f.events, ev];
      const event_addons = { ...f.event_addons };
      if (has) delete event_addons[ev];
      return { ...f, events, event_addons };
    });

  const toggleAddon = (ev, ad) =>
    setForm((f) => {
      const cur = f.event_addons[ev] || [];
      const next = cur.includes(ad) ? cur.filter((x) => x !== ad) : [...cur, ad];
      return { ...f, event_addons: { ...f.event_addons, [ev]: next } };
    });

  const buildMessage = (f) => {
    const addons = Object.entries(f.event_addons)
      .filter(([, list]) => list && list.length > 0)
      .map(([ev, list]) => `${ev}: ${list.join(", ")}`)
      .join(" · ");
    const date = f.wedding_date
      ? new Date(`${f.wedding_date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
      : "";
    return [
      "New Wedding Enquiry — The Big Wedd Website",
      "",
      `Bride's Name: ${f.bride_name}`,
      `Groom's Name: ${f.groom_name}`,
      `Contact Number: ${f.phone}`,
      `Alternate Contact Number: ${f.alt_phone || "—"}`,
      `Email ID: ${f.email || "—"}`,
      `Based Out Of (City): ${f.city || "—"}`,
      `Wedding Date: ${date || "—"}`,
      `Budget Range: ${f.budget_range || "—"}`,
      `Type Of Wedding: ${f.wedding_type || "—"}`,
      `Preferred Location: ${f.preferred_location || "—"}`,
      `Selected Events: ${f.events.length ? f.events.join(", ") : "—"}`,
      `Event Add-Ons: ${addons || "—"}`,
    ].join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildMessage(form))}`;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) window.location.href = url;
    toast.success("Opening WhatsApp — just press send to deliver your enquiry.");
  };

  return (
    <form onSubmit={submit} data-testid="enquire-form" className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Bride's Name <span className="text-gold">*</span></span>
          <input type="text" required value={form.bride_name} onChange={(e) => set("bride_name", e.target.value)} data-testid="enquire-input-bride-name" className={inputCls} placeholder="Bride's full name" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Groom's Name <span className="text-gold">*</span></span>
          <input type="text" required value={form.groom_name} onChange={(e) => set("groom_name", e.target.value)} data-testid="enquire-input-groom-name" className={inputCls} placeholder="Groom's full name" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Contact Number <span className="text-gold">*</span></span>
          <input type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="enquire-input-phone" className={inputCls} placeholder="+91" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Alternate Contact Number</span>
          <input type="tel" value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)} data-testid="enquire-input-alt-phone" className={inputCls} placeholder="Optional" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Email ID</span>
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="enquire-input-email" className={inputCls} placeholder="you@email.com" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Based Out Of (Which City)</span>
          <input type="text" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="enquire-input-city" className={inputCls} placeholder="e.g. Delhi" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Wedding Date</span>
          <input type="date" value={form.wedding_date} onChange={(e) => set("wedding_date", e.target.value)} data-testid="enquire-input-wedding-date" className={inputCls} />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Budget Range</span>
          <select value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)} data-testid="enquire-select-budget-range" className={`${inputCls} appearance-none cursor-pointer`}>
            <option value="">Select budget range</option>
            {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Type Of Wedding</span>
        <div className="mt-3 flex flex-wrap gap-2.5">
          {WEDDING_TYPES.map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => set("wedding_type", form.wedding_type === t ? "" : t)}
              data-testid={`enquire-chip-wedding-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.12em] transition-all duration-300 ${form.wedding_type === t ? "border-emerald bg-emerald text-ivory" : "border-gold/40 text-stone2 hover:border-gold hover:text-gold-text"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Preferred Location</span>
        <div className="mt-3 flex flex-wrap gap-2.5">
          {PREFERRED_LOCATIONS.map((l) => (
            <button
              type="button"
              key={l}
              onClick={() => set("preferred_location", form.preferred_location === l ? "" : l)}
              data-testid={`enquire-chip-location-${l.toLowerCase()}`}
              className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.12em] transition-all duration-300 ${form.preferred_location === l ? "border-emerald bg-emerald text-ivory" : "border-gold/40 text-stone2 hover:border-gold hover:text-gold-text"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-stone2">Select Your Events</span>
        <p className="mt-1 text-[10px] text-stone2/60">Choose the functions you're planning — add-ons are optional.</p>
        <div className="mt-3 flex flex-wrap gap-2.5">
          {EVENT_OPTIONS.map((ev) => (
            <button
              type="button"
              key={ev}
              onClick={() => toggleEvent(ev)}
              data-testid={`enquire-chip-event-${ev.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.12em] transition-all duration-300 ${form.events.includes(ev) ? "border-emerald bg-emerald text-ivory" : "border-gold/40 text-stone2 hover:border-gold hover:text-gold-text"}`}
            >
              {ev}
            </button>
          ))}
        </div>

        {form.events.length > 0 && (
          <div className="mt-5 space-y-4 border border-gold/25 bg-sand/60 p-5" data-testid="enquire-event-addons">
            {form.events.map((ev) => (
              <div key={ev} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                <span className="min-w-36 font-serif text-lg italic text-emerald">{ev}</span>
                <div className="flex flex-wrap gap-2">
                  {ADDON_OPTIONS.map((ad) => {
                    const active = (form.event_addons[ev] || []).includes(ad);
                    return (
                      <button
                        type="button"
                        key={ad}
                        onClick={() => toggleAddon(ev, ad)}
                        data-testid={`enquire-chip-addon-${ev.toLowerCase().replace(/\s+/g, "-")}-${ad.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`rounded-full border px-3.5 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-all duration-300 ${active ? "border-gold bg-gold text-ink" : "border-gold/40 text-stone2 hover:border-gold hover:text-gold-text"}`}
                      >
                        + {ad}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <p className="text-[10px] italic text-stone2/60">All add-ons are optional — nothing is compulsory.</p>
          </div>
        )}
      </div>

      <button
        type="submit"
        data-testid="enquire-submit"
        className="inline-flex w-full items-center justify-center rounded-full bg-gold px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-ink transition-all duration-500 hover:bg-emerald hover:text-gold-light"
      >
        Start Planning
      </button>
    </form>
  );
}
